<template>
  <div class="w-full">
    <!-- Header con título, filtros y ordenar - NO STICKY (el scroll es del contenedor padre) -->
    <div class="bg-white px-2 sm:px-6 pt-2 sm:pt-6 pb-2 border-b border-gray-100">
      <div class="flex flex-row justify-between items-center gap-2 sm:gap-4">
      <h2 class="text-base sm:text-xl font-bold text-gray-900 leading-snug">
        <span v-if="isLoading && isColivingSearch" class="text-gray-500 font-medium">
          {{ $t('searchResults.loading') }}
        </span>
        <span v-else-if="isColivingSearch && totalResults > 0">
          {{ $t('searchResults.resultsPlural', { count: totalResults }) }}
        </span>
        <span v-else-if="isColivingSearch && propertyLocation">
          {{ $t('searchResults.resultsPlural', { count: searchResults.length }) }}
        </span>
        <span v-else-if="propertyLocation">
          {{ $t('searchResults.countIn', { count: searchResults.length, location: propertyLocation }) }}
        </span>
        <span v-else-if="isColivingSearch">
          {{ $t('searchResults.resultsPlural', { count: searchResults.length }) }}
        </span>
        <span v-else>
          {{ $t('searchResults.count', { count: searchResults.length }) }}
        </span>
      </h2>
      
      <!-- Botón Filtros avanzados + Selector de ordenación -->
      <div v-if="searchResults.length > 0" class="flex items-center gap-2">
        <!-- Botón de Filtros Avanzados (solo visible en desktop) -->
        <button 
          type="button" 
          @click="emit('toggle-filters')"
          class="hidden md:flex items-center gap-1.5 px-3 py-1.5 sm:py-2 text-xs sm:text-sm font-medium text-gray-700 "
          :class="{ 'border-indigo-500 bg-indigo-50 text-indigo-700': showAdvancedFilters }"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd"
              d="M3 3a1 1 0 011-1h12a1 1 0 011 1v3a1 1 0 01-.293.707L12 11.414V15a1 1 0 01-.293.707l-2 2A1 1 0 018 17v-5.586L3.293 6.707A1 1 0 013 6V3z"
              clip-rule="evenodd" />
          </svg>
          <span class="hidden sm:inline">{{ $t('searchResults.filters') }}</span>
          <span v-if="activeFiltersCount > 0"
            class="px-1.5 py-0.5 text-xs font-semibold text-white bg-indigo-600 rounded-full min-w-[18px] text-center">
            {{ activeFiltersCount }}
          </span>
        </button>
      
      <!-- Selector de ordenación -->
        <div class="flex items-center gap-1 sm:gap-2">
        <label for="sortOrder" class="text-xs sm:text-sm font-medium text-gray-600 whitespace-nowrap hidden sm:inline">{{ $t('searchResults.sortBy') }}</label>
        <select 
          id="sortOrder" 
          v-model="sortOrder" 
          class="text-xs sm:text-sm border-gray-300 rounded-lg shadow-sm focus:border-indigo-500 focus:ring focus:ring-indigo-500 focus:ring-opacity-50 transition-colors duration-150 py-1.5 sm:py-2"
          @change="handleSortChange"
        >
          <!-- Opción de recomendados (solo si hay usuario logueado con preferencias) -->
          <option v-if="isColivingSearch && hasRecommendations" value="recommended">Recomendados</option>
          <option v-if="isColivingSearch" value="market_relevance">{{ $t('searchResults.sort.marketRelevance') }}</option>
          <!-- Opciones comunes -->
          <option value="date_desc">{{ $t('searchResults.sort.newest') }}</option>
          <option value="price_asc">{{ $t('searchResults.sort.priceAsc') }}</option>
          <option value="price_desc">{{ $t('searchResults.sort.priceDesc') }}</option>
          <!-- Opciones específicas para colivings -->
          <template v-if="isColivingSearch">
            <option value="stay_asc">{{ $t('searchResults.sort.stayAsc') }}</option>
          </template>
          <!-- Opción de disponibilidad para contratos tradicionales -->
          <option v-if="!isColivingSearch" value="availability">{{ $t('searchResults.sort.availability') }}</option>
        </select>
      </div>
    </div>
    </div>
    </div>

    <!-- Panel de Filtros Avanzados (aparece debajo del header cuando showAdvancedFilters = true) -->
    <transition
      enter-active-class="transition-all duration-200 ease-out"
      enter-from-class="opacity-0 max-h-0"
      enter-to-class="opacity-100 max-h-[420px]"
      leave-active-class="transition-all duration-200 ease-in"
      leave-from-class="opacity-100 max-h-[420px]"
      leave-to-class="opacity-0 max-h-0"
    >
      <div v-if="showAdvancedFilters" class="bg-gray-50 border-b border-gray-200 overflow-hidden flex flex-col">
        <!-- Área scrolleable de filtros -->
        <div class="px-4 sm:px-6 py-3 overflow-y-auto flex-1" style="max-height: 320px;">
          <!-- Contenido del panel de filtros será pasado por slot desde el padre -->
          <slot name="filters-panel"></slot>
        </div>
        
        <!-- Botones de acción FIJOS al final -->
        <div class="px-4 sm:px-6 py-3 border-t border-gray-200 flex-shrink-0">
          <slot name="filters-actions"></slot>
        </div>
      </div>
    </transition>

    <div v-if="$slots['lead-capture']" class="px-2 sm:px-6 pt-3 pb-1">
      <slot name="lead-capture" />
    </div>
    
    <!-- Contenido scrolleable - Ahora el scroll es de toda la página -->
    <div class="px-2 sm:px-6 py-4 space-y-4">
    <div v-if="isLoading" class="px-2 sm:px-6 py-4">
      <SearchResultsSkeleton
        :count="props.isMobile ? 4 : 8"
        :columns="props.isColivingSearch ? (props.mapVisible ? 3 : 4) : 2"
      />
    </div>

    <div v-else-if="searchError" class="text-center text-red-600 bg-red-100 p-4 rounded-lg">
      {{ searchError }}
    </div>

    <!-- Grid uniforme (sin destacados) + preview in-feed ads -->
    <template v-else-if="searchResults.length > 0">
    <div 
         class="grid grid-cols-1 gap-4"
         :class="isColivingSearch ? (mapVisible ? 'lg:grid-cols-3' : 'lg:grid-cols-4') : 'lg:grid-cols-2'">
      <template v-for="(contract, idx) in paginatedResults" :key="contract.id">
        <ContractCard 
          :contract="contract"
          :result-index="idx"
          :ref="el => { if (el) resultCardRefs[contract.id] = el }" 
          :class="{ 'ring-2 ring-indigo-500 shadow-lg': highlightedResultId === contract.id }"
          :is-current-user-interested="contract.currentUserIsInterested"
          :isMobile="props.isMobile"
          :is-coliving="contract.contractType === 'coliving'"
          :is-coliving-search-result="props.isColivingSearch"
          :vecy-rating="vecyRatingsByContractId[contract.id] ?? null"
          :isSaved="savedColivingIds.has(contract.id)"
          :showSaveButton="isColivingSearch"
          @toggleSave="handleToggleSave"
          @request-scraped-lead="emit('request-scraped-lead', $event)"
          @request-hive-lead="emit('request-hive-lead', $event)"
        />
        <!-- PREVIEW AdSense in-feed (local) — desactivado para prod
        <GoogleAdPlaceholder
          v-if="showAdPreview && shouldShowInFeedAd(idx)"
          variant="in-feed"
          :slot-index="inFeedAdIndex(idx)"
        />
        -->
      </template>
    </div>

    <!-- Botón "Cargar más" y contador -->
    <div v-if="hasMoreResults || hasMorePages" class="flex flex-col items-center gap-3 py-6">
      <p class="text-sm text-gray-500">
        <span v-if="totalResults > 0">
          {{ $t('searchResults.showing', { current: paginatedResults.length, total: totalResults }) }}
        </span>
        <span v-else>
          {{ $t('searchResults.showingSimple', { current: paginatedResults.length }) }}
        </span>
      </p>
      <button
        @click="loadMore"
        :disabled="isLoadingMore"
        class="px-6 py-3 bg-white border-2 border-indigo-600 text-indigo-600 rounded-full font-medium hover:bg-indigo-50 transition-colors duration-200 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <span v-if="!isLoadingMore">{{ $t('searchResults.loadMore') }}</span>
        <span v-else>{{ $t('searchResults.loadingMore') }}</span>
        <svg v-if="!isLoadingMore" xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
        </svg>
        <svg v-else class="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      </button>
    </div>
    
    <!-- Mensaje de fin de resultados -->
    <div v-if="!hasMoreResults && searchResults.length > itemsPerPage" class="text-center py-4">
      <p class="text-sm text-gray-500">
        {{ $t('searchResults.allResultsShown', { count: searchResults.length }) }}
      </p>
    </div>
    
    <!-- Disclaimer de datos scrapeados -->
    <div v-if="isColivingSearch && !hasMoreResults" class="mt-8 mb-4">
      <div class="bg-gray-50 border border-gray-200 rounded-xl p-5">
        <div class="flex items-start gap-3">
          <div class="flex-shrink-0">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-gray-400 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div>
            <h4 class="text-sm font-semibold text-gray-700 mb-2">{{ $t('searchResults.disclaimer.title') }}</h4>
            <ul class="text-xs text-gray-500 space-y-1.5 list-disc list-inside">
              <li v-html="$t('searchResults.disclaimer.sourceInfo')"></li>
              <li v-html="$t('searchResults.disclaimer.notAffiliatedWarning')"></li>
              <li v-html="$t('searchResults.disclaimer.dataAccuracy')"></li>
              <li v-html="$t('searchResults.disclaimer.noGuarantee')"></li>
              <li v-html="$t('searchResults.disclaimer.imagesOwnership')"></li>
              <li v-html="$t('searchResults.disclaimer.redirectNotice')"></li>
            </ul>
            <p class="text-xs text-gray-400 mt-3 pt-3 border-t border-gray-200">
              {{ $t('searchResults.disclaimer.contactPrefix') }}
              <a href="mailto:hola@coolliving.es" class="text-indigo-500 hover:text-indigo-600 underline">{{ $t('searchResults.disclaimer.contactLink') }}</a>.
            </p>
          </div>
        </div>
      </div>
    </div>
    </template>

    <div v-else-if="searchPerformed" class="text-center text-gray-500 bg-gray-100 p-6 rounded-lg mt-4">
      {{ $t('searchResults.noResults') }}
    </div>

    <div v-else class="text-center text-gray-500 p-6 mt-4">
      {{ $t('searchResults.instructions') }}
    </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, computed, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import ContractCard from './ContractCard.vue';
import SearchResultsSkeleton from './SearchResultsSkeleton.vue';
import { buildVecyAddress, getVecyRatingsBatch } from '@/services/vecyService';
// Preview AdSense (local): import GoogleAdPlaceholder from './ads/GoogleAdPlaceholder.vue';
// import { AD_IN_FEED_INTERVAL, AD_IN_FEED_INTERVAL_MOBILE } from '@/config/adPreview';
import { SEARCH_LIST_ITEMS_PER_PAGE } from '@/utils/searchListPagination';

const { t } = useI18n();

// Props recibidas del padre
const props = defineProps({
  isLoading: Boolean,
  searchError: String,
  searchResults: {
    type: Array,
    required: true,
    default: () => []
  },
  searchPerformed: Boolean,
  highlightedResultId: String,
  propertyLocation: String, // Para mostrar "en {{ location }}"
  isMobile: Boolean, // Aceptar la prop isMobile
  isColivingSearch: {
    type: Boolean,
    default: false
  },
  defaultSortOrder: {
    type: String,
    default: 'date_desc'
  },
  savedColivingIds: {
    type: Set,
    default: () => new Set()
  },
  hasMorePages: { // NUEVO: Indica si hay más páginas disponibles en el backend
    type: Boolean,
    default: false
  },
  isLoadingMore: { // NUEVO: Indica si está cargando más resultados
    type: Boolean,
    default: false
  },
  totalResults: { // NUEVO: Total de resultados disponibles
    type: Number,
    default: 0
  },
  listPage: {
    type: Number,
    default: 1,
  },
  showAdvancedFilters: { // NUEVO: Estado de visibilidad de filtros avanzados
    type: Boolean,
    default: false
  },
  activeFiltersCount: { // NUEVO: Número de filtros activos
    type: Number,
    default: 0
  },
  mapVisible: { // NUEVO: Indica si el mapa está visible (solo desktop)
    type: Boolean,
    default: true
  },
  // showAdPreview: { type: Boolean, default: false }, // PREVIEW AdSense (local)
});

// Estado local para la ordenación
const sortOrder = ref(props.defaultSortOrder);

// Watch para sincronizar el sortOrder local con la prop del padre
watch(() => props.defaultSortOrder, (newOrder) => {
  sortOrder.value = newOrder;
});

// Computed: verificar si hay resultados con recomendaciones
const hasRecommendations = computed(() => {
  return props.searchResults.some(result => result.matchData && result.matchData.score > 0);
});

const vecyRatingsByAddress = ref({});
let vecyLoadGeneration = 0;

function scheduleWhenIdle(run) {
  if (typeof requestIdleCallback !== 'undefined') {
    requestIdleCallback(run, { timeout: 2500 });
  } else {
    setTimeout(run, 0);
  }
}

async function loadVecyRatingsForResults(results) {
  if (!props.isColivingSearch || !results?.length) return;

  const cached = vecyRatingsByAddress.value;
  const addresses = [...new Set(
    results.map(buildVecyAddress).filter(Boolean),
  )].filter((addr) => !cached[addr]);

  if (addresses.length === 0) return;

  const generation = ++vecyLoadGeneration;

  try {
    const ratings = await getVecyRatingsBatch(addresses);
    if (generation !== vecyLoadGeneration) return;

    vecyRatingsByAddress.value = {
      ...vecyRatingsByAddress.value,
      ...ratings,
    };
  } catch {
    // Fallo silencioso: la búsqueda sigue funcionando sin ratings VeCy
  }
}

// ============================================
// PAGINACIÓN (controlada por el padre vía listPage + URL ?page=N)
// ============================================
const itemsPerPage = SEARCH_LIST_ITEMS_PER_PAGE;

// Computed: resultados paginados
const paginatedResults = computed(() => {
  const endIndex = props.listPage * itemsPerPage;
  return props.searchResults.slice(0, endIndex);
});

const vecyRatingsByContractId = computed(() => {
  if (!props.isColivingSearch) return {};

  const map = {};
  for (const contract of paginatedResults.value) {
    const address = buildVecyAddress(contract);
    if (!address) continue;

    const rating = vecyRatingsByAddress.value[address];
    if (rating) {
      map[contract.id] = rating;
    }
  }
  return map;
});

watch(
  paginatedResults,
  (results) => {
    if (!props.isColivingSearch || !results?.length) return;
    scheduleWhenIdle(() => loadVecyRatingsForResults(results));
  },
  { immediate: true },
);

// Computed: si hay más resultados por cargar (local o remoto)
const hasMoreResults = computed(() => {
  const itemsShown = props.listPage * itemsPerPage;
  const hasLocalMore = itemsShown < props.searchResults.length;
  const hasRemoteMore = props.totalResults > 0
    ? itemsShown < props.totalResults
    : props.hasMorePages;
  return hasLocalMore || hasRemoteMore;
});

// Eventos emitidos al padre
const emit = defineEmits(['card-mouseenter', 'card-mouseleave', 'sort-change', 'toggleSave', 'load-more', 'toggle-filters', 'update:listPage', 'request-scraped-lead', 'request-hive-lead']);

const loadMore = () => {
  emit('update:listPage', props.listPage + 1);
};

/* PREVIEW AdSense in-feed (local) — desactivado para prod
const inFeedInterval = computed(() =>
  props.isMobile ? AD_IN_FEED_INTERVAL_MOBILE : AD_IN_FEED_INTERVAL
)

function shouldShowInFeedAd(idx) {
  const n = idx + 1
  const interval = inFeedInterval.value
  return n % interval === 0 && n < paginatedResults.value.length
}

function inFeedAdIndex(idx) {
  return Math.floor((idx + 1) / inFeedInterval.value)
}
*/

// Función para manejar el toggle de guardado
const handleToggleSave = (contract) => {
  emit('toggleSave', contract);
};

// ── Tracking de impresiones en búsqueda ──────────────────────────────────
const trackedImpressionIds = new Set()
const pendingImpressions = []
let impressionFlushTimer = null
const IMPRESSION_FLUSH_MS = 2000

function flushImpressionBeacon() {
  impressionFlushTimer = null
  if (pendingImpressions.length === 0) return
  const batch = pendingImpressions.splice(0, pendingImpressions.length)
  try {
    const blob = new Blob([JSON.stringify({ impressions: batch })], { type: 'application/json' })
    if (navigator.sendBeacon) {
      navigator.sendBeacon('/api/analytics/search-impressions', blob)
    } else {
      fetch('/api/analytics/search-impressions', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ impressions: batch }), keepalive: true,
      }).catch(() => { /* non-blocking */ })
    }
  } catch { /* non-blocking */ }
}

function queueImpressions(results) {
  if (!props.isColivingSearch || !results.length) return
  let added = false
  results.forEach((r, idx) => {
    if (!r.id || trackedImpressionIds.has(r.id)) return
    trackedImpressionIds.add(r.id)
    pendingImpressions.push({
      colivingId: r.id,
      title: r.title || r.propertyTitle || '',
      position: idx,
      isExternal: !!r.isExternal,
    })
    added = true
  })
  if (!added) return
  if (!impressionFlushTimer) {
    impressionFlushTimer = setTimeout(flushImpressionBeacon, IMPRESSION_FLUSH_MS)
  }
}

watch(paginatedResults, (results) => {
  if (results.length) queueImpressions(results)
}, { immediate: true })

onBeforeUnmount(() => {
  if (impressionFlushTimer) {
    clearTimeout(impressionFlushTimer)
    flushImpressionBeacon()
  }
})

// Refs para las tarjetas (para hacer scroll)
const resultCardRefs = ref({});

watch(() => props.searchResults, () => {
  resultCardRefs.value = {};
});

// Método para hacer scroll a una tarjeta específica (expuesto al padre)
const scrollToCard = (id) => {
  // Si la tarjeta no está visible, cargar más páginas hasta encontrarla
  const targetIndex = props.searchResults.findIndex(r => r.id === id);
  if (targetIndex >= 0) {
    const requiredPage = Math.ceil((targetIndex + 1) / itemsPerPage);
    if (requiredPage > props.listPage) {
      emit('update:listPage', requiredPage);
    }
  }
  
  // Usar nextTick implícito con setTimeout para esperar el render
  setTimeout(() => {
    const cardComponent = resultCardRefs.value[id];
    if (cardComponent && cardComponent.$el && cardComponent.$el.scrollIntoView) {
      cardComponent.$el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, 100);
};

// Método para manejar el cambio en la ordenación
const handleSortChange = () => {
  emit('update:listPage', 1);
  emit('sort-change', sortOrder.value);
};

// Exponer el método scrollToCard para que el padre pueda llamarlo
defineExpose({ 
  scrollToCard 
});
</script>

<style scoped>
/* Estilos específicos para la lista si fueran necesarios */
/* Estilos del scrollbar (movidos del padre) */
.overflow-y-auto::-webkit-scrollbar {
  width: 8px;
}
.overflow-y-auto::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}
.overflow-y-auto::-webkit-scrollbar-thumb {
  background: #c5c5c5;
  border-radius: 10px;
}
.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}
</style> 